import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useParams } from 'react-router-dom'
import { useState } from 'react'
import {
  ArrowLeft, ChevronDown, Copy,
} from 'lucide-react'

/**
 * PARAGON Migration Jobs + DLQ Resolution Dashboard
 *
 * Default view: jobs list with status pipeline and DLQ drill-down links.
 * DLQ view: per-job dead-letter queue with payload inspection, retry/discard.
 *
 * NOTE: The `paragon_migration_dlq` and `paragon_migration_jobs` tables are
 * defined in migration 020 but the generated TypeScript types may not include
 * them until `supabase gen types` is re-run after the migration is applied.
 * Types have been regenerated — using typed supabase client.
 */

interface DlqItem {
  id: string
  job_id: string
  tenant_id: string
  target_table: string
  raw_payload: string | Record<string, unknown>
  error_reason: string
  status: 'pending' | 'retried' | 'ignored'
  resolved_at: string | null
  created_at: string
}

interface MigrationJob {
  id: string
  tenant_id: string
  tenant_name?: string
  legacy_system: string
  started_at?: string
  total_rows: number
  processed_rows: number
  error_count: number
  status: string
}

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  completed:              { label: 'Completed',   cls: 'text-[rgba(52,211,153,0.7)] bg-[rgba(52,211,153,0.06)] border-[rgba(52,211,153,0.1)]' },
  completed_with_errors:  { label: 'W/ Errors',   cls: 'text-[rgba(251,191,36,0.7)] bg-[rgba(251,191,36,0.06)] border-[rgba(251,191,36,0.1)]' },
  running:                { label: 'Running',      cls: 'text-[rgba(99,102,241,0.7)] bg-[rgba(99,102,241,0.06)] border-[rgba(99,102,241,0.1)]' },
  failed:                 { label: 'Failed',       cls: 'text-[rgba(248,113,113,0.7)] bg-[rgba(248,113,113,0.06)] border-[rgba(248,113,113,0.1)]' },
  queued:                 { label: 'Queued',       cls: 'text-zinc-400 dark:text-white/30 bg-black/[0.03] dark:bg-white/[0.03] border-black/[0.07] dark:border-white/[0.06]' },
}

type JobStatus = 'all' | 'running' | 'completed' | 'completed_with_errors' | 'failed' | 'queued'

function MigrationHeader({
  view,
  selectedJob,
  selectedCount,
  onBack,
  onRetry,
  onDiscard,
  retryPending,
  discardPending,
}: {
  view: 'jobs' | 'dlq'
  selectedJob: MigrationJob | null
  selectedCount: number
  onBack: () => void
  onRetry: () => void
  onDiscard: () => void
  retryPending: boolean
  discardPending: boolean
}) {
  return (
    <div className="h-14 flex items-center justify-between px-6 border-b border-black/[0.06] dark:border-white/[0.04] flex-shrink-0 bg-zinc-50 dark:bg-transparent">
      <div className="flex items-center gap-3">
        {view === 'dlq' && (
          <button onClick={onBack} aria-label="Back to migration jobs" className="p-1.5 rounded-lg hover:bg-black/[0.04] dark:hover:bg-white/[0.04] text-zinc-400 dark:text-white/25 hover:text-zinc-600 dark:hover:text-white/50 transition-colors mr-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/40">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <span className="text-[14px] text-zinc-900 dark:text-zinc-50 font-medium">
          {view === 'jobs' ? 'Migration Jobs' : `DLQ - ${selectedJob?.tenant_name ?? selectedJob?.tenant_id?.slice(0, 8) ?? 'Job'}`}
        </span>
        {view === 'dlq' && selectedJob && <span className="text-[11px] font-mono text-zinc-400 dark:text-white/15">{selectedJob.id.slice(0, 8)}</span>}
      </div>

      {view === 'dlq' && selectedCount > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-zinc-400 dark:text-white/25 font-mono">{selectedCount} selected</span>
          <button onClick={onRetry} disabled={retryPending} className="px-3 py-1.5 rounded-lg text-[11px] text-[rgba(99,102,241,0.8)] bg-[rgba(99,102,241,0.08)] border border-[rgba(99,102,241,0.15)] hover:bg-[rgba(99,102,241,0.12)] transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35">Retry</button>
          <button onClick={onDiscard} disabled={discardPending} className="px-3 py-1.5 rounded-lg text-[11px] text-[rgba(248,113,113,0.7)] bg-[rgba(248,113,113,0.06)] border border-[rgba(248,113,113,0.12)] hover:bg-[rgba(248,113,113,0.1)] transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35">Discard</button>
        </div>
      )}
    </div>
  )
}

function JobsView({
  jobCounts,
  statusFilter,
  setStatusFilter,
  jobsLoading,
  filteredJobs,
  onOpenDlq,
  formatTime,
}: {
  jobCounts: { total: number; running: number; completed: number; failed: number; dlqDepth: number }
  statusFilter: JobStatus
  setStatusFilter: (status: JobStatus) => void
  jobsLoading: boolean
  filteredJobs: MigrationJob[]
  onOpenDlq: (job: MigrationJob) => void
  formatTime: (ts: string | null | undefined) => string
}) {
  return (
    <>
      <div className="grid grid-cols-5 gap-px mb-6 rounded-xl overflow-hidden border border-black/[0.08] dark:border-white/[0.06]">
        {[
          { label: 'Total Jobs', value: String(jobCounts.total), cls: 'text-zinc-900 dark:text-zinc-50' },
          { label: 'Running', value: String(jobCounts.running), cls: 'text-[rgba(99,102,241,0.8)]' },
          { label: 'Completed', value: String(jobCounts.completed), cls: 'text-[rgba(52,211,153,0.8)]' },
          { label: 'Failed', value: String(jobCounts.failed), cls: 'text-[rgba(248,113,113,0.8)]' },
          { label: 'DLQ Depth', value: String(jobCounts.dlqDepth), cls: 'text-[rgba(251,191,36,0.8)]' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-black/[0.02] dark:bg-white/[0.02] p-4 hover:bg-black/[0.04] dark:hover:bg-white/[0.035] transition-colors">
            <div className="text-[11px] text-zinc-400 dark:text-white/25 mb-2">{kpi.label}</div>
            <div className={`text-[22px] font-mono font-light leading-none ${kpi.cls}`}>{kpi.value}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1 mb-4">
        {(['all', 'running', 'completed', 'completed_with_errors', 'failed', 'queued'] as JobStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            aria-pressed={statusFilter === status}
            className={`text-[10px] font-mono uppercase px-2.5 py-1 rounded transition-[background-color,color,border-color,width] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35 ${
              statusFilter === status
                ? 'bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.2)] text-[#C9A84C]'
                : 'text-zinc-400 dark:text-white/25 border border-transparent hover:bg-black/[0.04] dark:hover:bg-white/[0.03]'
            }`}
          >
            {status === 'completed_with_errors' ? 'W/ Errors' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {jobsLoading ? (
        <div className="text-center text-zinc-400 dark:text-white/20 py-12 text-[13px] font-mono">Loading jobs...</div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center text-zinc-400 dark:text-white/15 py-12 text-[13px]">No jobs match filter</div>
      ) : (
        <div className="space-y-1">
          {filteredJobs.map((job) => {
            const badge = STATUS_MAP[job.status] ?? STATUS_MAP.queued
            const progress = job.total_rows > 0 ? (job.processed_rows / job.total_rows) * 100 : 0
            return (
              <div key={job.id} className="flex items-center rounded-lg border border-black/[0.07] dark:border-white/[0.05] bg-black/[0.02] dark:bg-white/[0.02] hover:bg-black/[0.04] dark:hover:bg-white/[0.03] transition-colors">
                <div className="w-[100px] flex-shrink-0 py-3 pl-4 text-[11px] font-mono text-zinc-400 dark:text-white/25">{job.id.slice(0, 8)}</div>
                <div className="flex-1 py-3 min-w-0">
                  <div className="text-[13px] text-zinc-900 dark:text-zinc-50 truncate">{job.tenant_name ?? job.tenant_id.slice(0, 12)}</div>
                  <div className="text-[12px] text-zinc-500 dark:text-white/30 truncate">{job.legacy_system}</div>
                  <div className="text-[10px] font-mono text-zinc-400 dark:text-white/15 mt-0.5">{formatTime(job.started_at)} · {job.processed_rows}/{job.total_rows} rows</div>
                </div>

                {job.status === 'running' && (
                  <div className="w-[120px] flex-shrink-0 mx-3">
                    <div className="w-full h-1 rounded-full bg-black/[0.06] dark:bg-white/[0.06] overflow-hidden"><div className="h-full rounded-full bg-[rgba(99,102,241,0.5)] transition-[background-color,color,border-color,width]" style={{ width: `${progress}%` }} /></div>
                    <div className="text-[9px] font-mono text-zinc-400 dark:text-white/20 mt-0.5 text-right">{Math.round(progress)}%</div>
                  </div>
                )}

                <div className="flex-shrink-0 px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span></div>

                {job.error_count > 0 ? (
                  <div className="flex-shrink-0 pr-3"><button onClick={() => onOpenDlq(job)} className="text-[10px] px-2 py-0.5 rounded text-[rgba(251,191,36,0.6)] bg-[rgba(251,191,36,0.06)] border border-[rgba(251,191,36,0.1)] hover:bg-[rgba(251,191,36,0.1)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35">DLQ {job.error_count}</button></div>
                ) : (
                  <div className="w-3" />
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

function DlqView({
  allDlqSelected,
  onToggleAll,
  dlqLoading,
  dlqItems,
  expandedDlq,
  selectedDlqs,
  copiedId,
  onToggleItem,
  onToggleExpand,
  onCopyPayload,
  onRetryOne,
  onIgnoreOne,
  retryPending,
  ignorePending,
  formatTime,
  formatPayload,
}: {
  allDlqSelected: boolean
  onToggleAll: () => void
  dlqLoading: boolean
  dlqItems: DlqItem[]
  expandedDlq: string | null
  selectedDlqs: Set<string>
  copiedId: string | null
  onToggleItem: (id: string) => void
  onToggleExpand: (id: string) => void
  onCopyPayload: (item: DlqItem) => void
  onRetryOne: (id: string) => void
  onIgnoreOne: (id: string) => void
  retryPending: boolean
  ignorePending: boolean
  formatTime: (ts: string | null | undefined) => string
  formatPayload: (raw: string | Record<string, unknown>) => string
}) {
  return (
    <>
      <div className="flex items-center gap-3 px-4 py-2 mb-1 text-[10px] text-zinc-400 dark:text-white/15 font-mono tracking-wider">
        <input aria-label="Select all DLQ items" type="checkbox" checked={allDlqSelected} onChange={onToggleAll} className="w-4 h-4 rounded border border-black/20 dark:border-white/20 bg-transparent accent-[#C9A84C] cursor-pointer" />
        <span className="flex-1">ENTITY</span>
        <span className="w-[220px]">ERROR</span>
        <span className="w-[60px] text-right">RETRIES</span>
        <span className="w-[130px] text-right">FIRST SEEN</span>
        <span className="w-4" />
      </div>

      {dlqLoading ? (
        <div className="text-center text-zinc-400 dark:text-white/20 py-12 text-[13px] font-mono">Loading DLQ...</div>
      ) : dlqItems.length === 0 ? (
        <div className="text-center text-zinc-400 dark:text-white/15 py-12 text-[13px]">No DLQ items for this job</div>
      ) : (
        <div className="space-y-1">
          {dlqItems.map((item) => {
            const isExpanded = expandedDlq === item.id
            const isSelected = selectedDlqs.has(item.id)
            return (
              <div key={item.id} className={`rounded-lg border transition-[background-color,color,border-color,width] duration-150 ${isSelected ? 'border-[rgba(201,168,76,0.15)] bg-[rgba(201,168,76,0.02)]' : 'border-black/[0.07] dark:border-white/[0.05] bg-black/[0.02] dark:bg-white/[0.02]'}`}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <input aria-label={`Select DLQ item ${item.id.slice(0, 8)}`} type="checkbox" checked={isSelected} onChange={() => onToggleItem(item.id)} className="w-4 h-4 rounded border border-black/20 dark:border-white/20 bg-transparent accent-[#C9A84C] cursor-pointer flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono text-zinc-500 dark:text-white/40">{item.target_table}</div>
                    <div className="text-[10px] font-mono text-zinc-400 dark:text-white/15">{item.id.slice(0, 8)}</div>
                  </div>
                  <div className="w-[220px] flex-shrink-0"><div className="text-[12px] text-[rgba(248,113,113,0.7)] truncate">{item.error_reason}</div></div>
                  <div className="w-[60px] flex-shrink-0 text-right text-[11px] font-mono text-zinc-400 dark:text-white/25">-/3</div>
                  <div className="w-[130px] flex-shrink-0 text-right text-[10px] font-mono text-zinc-400 dark:text-white/15">{formatTime(item.created_at)}</div>
                   <button onClick={() => onToggleExpand(item.id)} aria-label={isExpanded ? 'Collapse payload details' : 'Expand payload details'} aria-expanded={isExpanded} aria-controls={`dlq-item-panel-${item.id}`} className="w-4 flex-shrink-0 text-zinc-400 dark:text-white/20 hover:text-zinc-600 dark:hover:text-white/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35"><ChevronDown className={`w-4 h-4 transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`} /></button>
                </div>

                {isExpanded && (
                  <div id={`dlq-item-panel-${item.id}`} className="px-4 pb-4 border-t border-black/[0.05] dark:border-white/[0.04]">
                    <div className="relative mt-3 rounded-lg bg-zinc-100 dark:bg-black/50 border border-black/[0.07] dark:border-white/[0.04] p-3">
                      <button onClick={() => onCopyPayload(item)} className="absolute top-2 right-2 p-1.5 rounded hover:bg-black/[0.05] dark:hover:bg-white/[0.05] text-zinc-400 dark:text-white/20 hover:text-zinc-600 dark:hover:text-white/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35" title="Copy payload" aria-label="Copy payload"><Copy className="w-3.5 h-3.5" /></button>
                      {copiedId === item.id && <span className="absolute top-2 right-9 text-[10px] font-mono text-[rgba(52,211,153,0.7)]">copied</span>}
                      <pre className="font-mono text-[11px] text-zinc-600 dark:text-white/40 overflow-x-auto max-h-48 whitespace-pre-wrap break-all">{formatPayload(item.raw_payload)}</pre>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] font-mono text-zinc-400 dark:text-white/15">Last retry: {item.resolved_at ? formatTime(item.resolved_at) : '-'}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => onRetryOne(item.id)} disabled={retryPending} className="px-3 py-1.5 rounded-lg text-[11px] text-[rgba(99,102,241,0.8)] bg-[rgba(99,102,241,0.08)] border border-[rgba(99,102,241,0.15)] hover:bg-[rgba(99,102,241,0.12)] transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35">Retry</button>
                        <button onClick={() => onIgnoreOne(item.id)} disabled={ignorePending} className="px-3 py-1.5 rounded-lg text-[11px] text-[rgba(248,113,113,0.7)] bg-[rgba(248,113,113,0.06)] border border-[rgba(248,113,113,0.12)] hover:bg-[rgba(248,113,113,0.1)] transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35">Discard</button>
                        <button className="px-3 py-1.5 rounded-lg text-[11px] text-zinc-500 dark:text-white/30 bg-black/[0.04] dark:bg-white/[0.03] border border-black/[0.07] dark:border-white/[0.06] hover:bg-black/[0.07] dark:hover:bg-white/[0.05] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/35">Source Record</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

export default function ParagonMigrationDLQ() {
  const { jobId: routeJobId } = useParams<{ jobId: string }>()
  const queryClient = useQueryClient()

  const [uiState, setUiState] = useState<{
    manualView: 'jobs' | 'dlq'
    selectedJob: MigrationJob | null
    statusFilter: JobStatus
  }>({
    manualView: routeJobId ? 'dlq' : 'jobs',
    selectedJob: null,
    statusFilter: 'all',
  })
  const [dlqState, setDlqState] = useState<{
    expandedDlq: string | null
    selectedDlqs: Set<string>
    copiedId: string | null
  }>({
    expandedDlq: null,
    selectedDlqs: new Set(),
    copiedId: null,
  })
  const { manualView, selectedJob, statusFilter } = uiState
  const { expandedDlq, selectedDlqs, copiedId } = dlqState

  const view: 'jobs' | 'dlq' = routeJobId ? 'dlq' : manualView

  // ── Jobs query ──────────────────────────────────────────────────
  const { data: allJobs = [], isLoading: jobsLoading } = useQuery({
    queryKey: ['paragon-jobs-all'],
    enabled: view === 'jobs',
    queryFn: async () => {
      const { data } = await supabase.from('paragon_migration_jobs')
        .select('*')
        .order('created_at', { ascending: false })
      return (data ?? []) as MigrationJob[]
    },
  })

  // ── DLQ items query ─────────────────────────────────────────────
  const activeJobId = routeJobId ?? selectedJob?.id
  const { data: dlqItems = [], isLoading: dlqLoading } = useQuery({
    queryKey: ['paragon-dlq', activeJobId],
    enabled: view === 'dlq' && !!activeJobId,
    queryFn: async () => {
      const { data, error } = await supabase.from('paragon_migration_dlq')
        .select('*')
        .eq('job_id', activeJobId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as DlqItem[]
    },
  })

  // ── Mutations ───────────────────────────────────────────────────
  const retryMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from('paragon_migration_dlq')
        .update({ status: 'retried', resolved_at: new Date().toISOString() })
        .in('id', ids)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paragon-dlq', activeJobId] })
      setDlqState(prev => ({ ...prev, selectedDlqs: new Set() }))
    },
  })

  const ignoreMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from('paragon_migration_dlq')
        .update({ status: 'ignored', resolved_at: new Date().toISOString() })
        .in('id', ids)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paragon-dlq', activeJobId] })
      setDlqState(prev => ({ ...prev, selectedDlqs: new Set() }))
    },
  })

  // ── Derived ─────────────────────────────────────────────────────
  const jobCounts = {
    total:    allJobs.length,
    running:  allJobs.filter(j => j.status === 'running').length,
    completed: allJobs.filter(j => j.status === 'completed').length,
    failed:   allJobs.filter(j => j.status === 'failed').length,
    dlqDepth: allJobs.reduce((acc, j) => acc + (j.error_count || 0), 0),
  }

  const filteredJobs = statusFilter === 'all'
    ? allJobs
    : allJobs.filter(j => j.status === statusFilter)

  const allDlqSelected = dlqItems.length > 0 && dlqItems.every(i => selectedDlqs.has(i.id))

  const toggleAllDlq = () => {
    if (allDlqSelected) setDlqState(prev => ({ ...prev, selectedDlqs: new Set() }))
    else setDlqState(prev => ({ ...prev, selectedDlqs: new Set(dlqItems.map(i => i.id)) }))
  }

  const copyPayload = async (item: DlqItem) => {
    const raw = typeof item.raw_payload === 'string'
      ? item.raw_payload
      : JSON.stringify(item.raw_payload, null, 2)
    try {
      const parsed = JSON.parse(raw)
      await navigator.clipboard.writeText(JSON.stringify(parsed, null, 2))
    } catch {
      await navigator.clipboard.writeText(raw)
    }
    setDlqState(prev => ({ ...prev, copiedId: item.id }))
    setTimeout(() => setDlqState(prev => ({ ...prev, copiedId: null })), 1500)
  }

  const formatPayload = (raw: string | Record<string, unknown>) => {
    try {
      const str = typeof raw === 'string' ? raw : JSON.stringify(raw)
      return JSON.stringify(JSON.parse(str), null, 2)
    } catch {
      return typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2)
    }
  }

  const formatTime = (ts: string | null | undefined) => {
    if (!ts) return '—'
    try { return new Date(ts).toLocaleString() } catch { return ts }
  }

  return (
      <div className="w-full h-full min-h-0 bg-zinc-50 dark:bg-[#171717] flex overflow-hidden transition-colors duration-200">
        <div className="flex-1 flex flex-col overflow-hidden">
          <MigrationHeader
            view={view}
            selectedJob={selectedJob}
            selectedCount={selectedDlqs.size}
            onBack={() => setUiState(prev => ({ ...prev, manualView: 'jobs', selectedJob: null }))}
            onRetry={() => retryMutation.mutate([...selectedDlqs])}
            onDiscard={() => ignoreMutation.mutate([...selectedDlqs])}
            retryPending={retryMutation.isPending}
            discardPending={ignoreMutation.isPending}
          />

          <div className="flex-1 overflow-y-auto p-6">
            {view === 'jobs' ? (
              <JobsView
                jobCounts={jobCounts}
                statusFilter={statusFilter}
                setStatusFilter={(status) => setUiState(prev => ({ ...prev, statusFilter: status }))}
                jobsLoading={jobsLoading}
                filteredJobs={filteredJobs}
                onOpenDlq={(job) => setUiState(prev => ({ ...prev, selectedJob: job, manualView: 'dlq' }))}
                formatTime={formatTime}
              />
            ) : (
              <DlqView
                allDlqSelected={allDlqSelected}
                onToggleAll={toggleAllDlq}
                dlqLoading={dlqLoading}
                dlqItems={dlqItems}
                expandedDlq={expandedDlq}
                selectedDlqs={selectedDlqs}
                copiedId={copiedId}
                onToggleItem={(id) => {
                  const next = new Set(selectedDlqs)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  setDlqState(prev => ({ ...prev, selectedDlqs: next }))
                }}
                onToggleExpand={(id) => setDlqState(prev => ({ ...prev, expandedDlq: prev.expandedDlq === id ? null : id }))}
                onCopyPayload={(item) => {
                  void copyPayload(item)
                }}
                onRetryOne={(id) => retryMutation.mutate([id])}
                onIgnoreOne={(id) => ignoreMutation.mutate([id])}
                retryPending={retryMutation.isPending}
                ignorePending={ignoreMutation.isPending}
                formatTime={formatTime}
                formatPayload={formatPayload}
              />
            )}
          </div>
        </div>
      </div>
  )
}
